import React, { PropsWithChildren, useState } from "react";
import { StaticImage } from "gatsby-plugin-image";
import { Link, type PageProps } from "gatsby";
import classNames from "classnames";
import { DecoratedSeparator } from "./DecoratedSeparator";
import { usePreventScrolling } from "../hooks/usePreventScrolling";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { Nav, NavItem } from "./Nav";
import { DetailsHover } from "./DetailsHover";

const navItems: NavItem[] = [
  {
    title: "Startseite",
    to: "/",
  },
  {
    title: "Über uns",
    to: "/ueber-uns/",
  },
  {
    title: "Pädagogisches Konzept",
    to: "/paedagogisches-konzept/",
  },
  {
    title: "Standorte",
    to: "/standorte/",
    children: [
      {
        title: "KiTa Die Grünschnäbel",
        to: "/standorte/kita-die-gruenschnaebel/",
      },
      {
        title: "KiTa Die 4 Elemente",
        to: "/standorte/kita-4-elemente/",
      },
    ],
  },
  {
    title: "Unser Alltag",
    to: "/alltag/",
    children: [
      {
        title: "Tagesablauf",
        to: "/alltag/tagesablauf/",
      },
      {
        title: "Eingewöhnung",
        to: "/alltag/eingewoehnung/",
      },
      {
        title: "Mahlzeiten",
        to: "/alltag/mahlzeiten/",
      },
    ],
  },
  {
    title: "Aktuelles",
    to: "/aktuelles/",
  },
  {
    title: "FAQ",
    to: "/faq/",
  },
  {
    title: "Kontakt",
    to: "/kontakt/",
  },
];

export default function Header({
  location,
  children,
  backgroundClassName,
}: PropsWithChildren<
  Pick<PageProps, "location"> & { backgroundClassName?: string }
>) {
  const onWheel = usePreventScrolling<HTMLDialogElement>();
  const [menuOpen, toggleMenuOpen] = useState(false);
  const closeMenu = () => toggleMenuOpen(false);

  useEscapeKey(closeMenu);

  return (
    <>
      <header className={"grid sticky top-0 z-10"}>
        <div
          className={classNames(
            // "sticky top-0 z-10",
            "flex justify-between items-end px-8 bg-white z-10 h-24",
            "border-b-2 border-primary xl:border-0",
          )}
        >
          <Link to={"/"} title="Fokus Familie gGmbH">
            <StaticImage
              src={"../images/branding-logo.png"}
              alt={"Fokus Familie gGmbH - Logo"}
              placeholder={"none"}
              width={220}
            />
          </Link>
          <button
            className={"2xl:hidden p-8 -mr-8"}
            onClick={() => toggleMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            title={menuOpen ? "Menü schließen" : "Menü öffnen"}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={"fill-primary"}
            >
              <g
                className={classNames({
                  hidden: menuOpen,
                })}
              >
                <rect
                  width="34"
                  height="4"
                  rx="2"
                  transform="matrix(1 0 0 -1 1 28)"
                />
                <rect
                  width="34"
                  height="4"
                  rx="2"
                  transform="matrix(1 0 0 -1 1 20)"
                />
                <rect
                  width="34"
                  height="4"
                  rx="2"
                  transform="matrix(1 0 0 -1 1 12)"
                />
              </g>
              <g
                className={classNames({
                  hidden: !menuOpen,
                })}
              >
                <rect
                  width="34"
                  height="4"
                  rx="2"
                  transform="matrix(0.707107 0.707107 0.707107 -0.707107 4 7.87006)"
                />
                <rect
                  width="34"
                  height="4"
                  rx="2"
                  transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 7 31.8701)"
                />
              </g>
            </svg>
          </button>

          <Nav
            items={navItems}
            location={location}
            onClickNavItem={() => toggleMenuOpen(false)}
            className={"hidden 2xl:flex h-full"}
            listClassName={
              "h-full items-center 2xl:flex flex-col lg:flex-row gap-4 text-base xl:text-xl"
            }
            listItemClassName={"h-full flex items-center"}
          />
        </div>
        <dialog
          open={menuOpen}
          onWheel={onWheel}
          className={classNames(
            "2xl:hidden",
            "fixed left-0 right-0 top-24 z-10",
            "w-full h-[calc(100vh_-_6rem)] overflow-auto",
            "grid gap-4 p-8",
            "transform transition-all origin-top",
            "bg-primary text-white font-bold text-xl",
            {
              "scale-y-0": !menuOpen,
            },
          )}
        >
          <Nav
            items={navItems}
            location={location}
            onClickNavItem={() => toggleMenuOpen(false)}
            listClassName={"flex flex-col gap-2"}
          />
        </dialog>
      </header>
      <div
        className={classNames(
          "hidden lg:flex",
          "h-16 bg-primary text-white justify-between items-start px-8",
        )}
      >
        <Link to={"/"} title="Fokus Familie gGmbH">
          <StaticImage
            src={"../images/branding-subline.png"}
            alt={"Fokus Familie gGmbH - Logo"}
            placeholder={"none"}
            width={220}
          />
        </Link>
        <div
          className={
            "flex self-stretch items-center text-base [&>*]:p-4 [&>*]:h-full [&>*]:flex [&>*]:items-center [&>*]:content-center"
          }
        >
          <DetailsHover
            summary={
              <div className={"flex gap-4 items-center"}>
                <FontAwesomeIcon icon={faPhone} />
                <FontAwesomeIcon icon={faEnvelope} />
                <FontAwesomeIcon icon={faClock} />
                <span>Kontakt und Öffnungszeiten</span>
              </div>
            }
          >
            <div className={"grid gap-4"}>
              <LocalBusiness
                name={"KiTa Die Grünschnäbel"}
                openingHours={{
                  content: "Mo-Do 07:00-16:15, Fr 07:00-15:00",
                  label: "Mo-Do 07:00 – 16:15 Uhr, Fr 07:00 – 15:00 Uhr",
                }}
                telephone={{
                  value: "004928419393260",
                  label: "02841-9393260",
                }}
                email={{
                  value: "info@fokus-familie.com",
                  label: "info@fokus-familie.com",
                }}
              />
              <hr className={"border-primary"} />
              <LocalBusiness
                name={"KiTa Die 4 Elemente"}
                openingHours={{
                  content: "Mo-Fr 07:00-16:00",
                  label: "Mo-Fr 07:00 – 16:00 Uhr",
                }}
                telephone={{
                  value: "004928429049347",
                  label: "02842-9049347",
                }}
                email={{
                  value: "info@fokus-familie.com",
                  label: "info@fokus-familie.com",
                }}
              />
              <hr className={"border-primary"} />
              <LocalBusiness
                name={"Die 4 Elemente"}
                label={"Büro"}
                openingHours={{
                  content: "Mo-Do 08:00–15:00, Fr 08:00–13:00",
                  label: "Mo-Do 08:00 – 15:00 Uhr, Fr 08:00 – 13:00 Uhr",
                }}
                telephone={{
                  value: "004928411735550",
                  label: "02841-1735550",
                }}
                email={{
                  value: "info@fokus-familie.com",
                  label: "info@fokus-familie.com",
                }}
              />
            </div>
          </DetailsHover>
          <a
            href={"https://www.facebook.com/FokusFamilieMoers/"}
            className={"gap-2"}
            rel={"noopener noreferrer"}
          >
            <FontAwesomeIcon icon={faFacebook} />
            <span className={"sr-only"}>Facebook</span>
          </a>
          <a
            href={"https://www.instagram.com/fokus_familie/"}
            className={"gap-2"}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            <FontAwesomeIcon icon={faInstagram} />
            <span className={"sr-only"}>Instagram</span>
          </a>
        </div>
      </div>

      <figure
        className={classNames("relative grid overflow-hidden pt-8", {
          "min-h-96": true,
        })}
      >
        <div className={"absolute inset-0 -z-10"}>
          <StaticImage
            src={"../images/medium-bgHeader.jpg"}
            alt={"Spielende Kinder"}
            layout={"fullWidth"}
            quality={80}
            placeholder={"blurred"}
            loading={"eager"}
            objectFit={"cover"}
            objectPosition={"center"}
            className={"max-h-full h-full"}
            transformOptions={{
              fit: "cover",
              cropFocus: "attention",
            }}
          />
        </div>
        <div
          className={"grid relative place-items-center grid-rows-[1fr,auto]"}
          style={{
            // By using the same grid area for both, they are stacked on top of each other
            gridArea: "1/1",
          }}
        >
          <div className={"grid items-center justify-center my-auto p-8"}>
            {children}
          </div>
          <div className={"transform translate-y-[1px] w-full"}>
            <DecoratedSeparator backgroundClassName={backgroundClassName} />
          </div>
        </div>
      </figure>
    </>
  );
}

function LocalBusiness(props: {
  name: string;
  label?: string;
  openingHours: {
    content: string;
    label: string;
  };
  telephone: {
    value: string;
    label: string;
  };
  email: {
    value: string;
    label: string;
  };
}) {
  return (
    <div
      itemScope
      itemType="https://schema.org/LocalBusiness"
      className={"grid gap-4"}
    >
      <h3 className={"font-bold"}>
        <span itemProp={"name"} content={props.name}>
          {props.label ?? props.name}
        </span>
      </h3>
      <span itemProp="openingHours" content={props.openingHours.content}>
        {props.openingHours.label}
      </span>
      <a
        href={"tel:" + props.telephone.value}
        className={"flex items-center gap-2"}
      >
        <FontAwesomeIcon icon={faPhone} />
        <span itemProp={"telephone"}>{props.telephone.label}</span>
      </a>
      <a
        href={"mailto:" + props.email.value}
        className={"flex items-center gap-2"}
      >
        <FontAwesomeIcon icon={faEnvelope} />
        <span itemProp={"email"}>{props.email.label}</span>
      </a>
    </div>
  );
}
